import PageHero from '../components/ui/PageHero';
import Reveal from '../components/ui/Reveal';
import directorPhoto from '../assets/images/team/amartya-modi.jpeg';
import './About.css';

const stats = [
  { value: '7', label: 'Core modules — one connected platform' },
  { value: '2', label: 'Companion apps — field crew & customer' },
  { value: '5', label: 'Service industries served today' },
  { value: '20+', label: "Years in business automation via our director" },
];

const principles = [
  {
    title: 'Contribution',
    detail: 'Every workflow we build exists to remove a real bottleneck from a technician’s or dispatcher’s day — not to add another dashboard to check.',
  },
  {
    title: 'Fairness',
    detail: 'Transparent pricing, clear job and billing trails, and data that stays yours. No hidden logic deciding what your team sees.',
  },
  {
    title: 'Teamwork',
    detail: 'Appointments, workforce, customers, and billing share one source of truth, so the office and the field are always reading from the same page.',
  },
  {
    title: 'Improvement',
    detail: 'We ship refinements continuously based on how service and maintenance teams actually use the platform, not on a fixed yearly roadmap.',
  },
  {
    title: 'Courtesy',
    detail: 'Support that listens first. Every ticket is a conversation with a person, not a queue position.',
  },
  {
    title: 'Adaptability',
    detail: 'From a single technician to a multi-city field team, the system scales with you instead of forcing a re-platform.',
  },
  {
    title: 'Gratitude',
    detail: 'We build for the service businesses who trusted us early — their feedback shaped nearly every module on this platform.',
  },
];

export default function About() {
  return (
    <div className="about-page">
      <PageHero
        eyebrow="About Tecnvirons"
        title="One system for every service job, from the first call to the closed invoice."
        body="We build the operations platform for engineering, maintenance, and field-service businesses — RO servicing, vending, AC & fridge repair, medical equipment upkeep, and civil construction — so nothing runs on memory or paper."
      />

      <section className="section">
        <div className="container">
          <div className="about-mission">
            <Reveal as="div">
              <span className="eyebrow">Our Mission</span>
              <h2>We think field service should run on one system, not five tools that don’t talk to each other.</h2>
              <p>
                Tecnvirons already runs an AI-powered ERP and CRM platform for service
                businesses across the US and India. Building this platform, we kept seeing the
                same pattern in engineering, maintenance, and construction businesses: a booking
                made over the phone, a job tracked on WhatsApp, a technician’s notebook for
                service history, and a spreadsheet for billing — four disconnected records for
                one job.
              </p>
              <p>
                Tecnvirons puts appointments, workforce management, customer records, inventory,
                orders, and recurring service contracts on one platform — with a field app for your
                crew (Tecnvi Ongo) and a self-service channel for your customers (Tecnvi Connect) on
                either side of it. Every feature we ship is judged against one question: does this
                remove a manual handoff, or add one?
              </p>
            </Reveal>

            <Reveal as="div" stagger className="about-stats">
              {stats.map((stat) => (
                <div key={stat.label} className="about-stat card reveal-item">
                  <strong>{stat.value}</strong>
                  <span>{stat.label}</span>
                </div>
              ))}
            </Reveal>
          </div>
        </div>
      </section>

      <section className="section section-alt">
        <div className="container">
          <Reveal className="section-head center">
            <span className="eyebrow">How We Work</span>
            <h2>Principles behind every build.</h2>
            <p>The same values that guide how we build every module on this platform.</p>
          </Reveal>

          <Reveal as="div" stagger className="principles-grid">
            {principles.map((p) => (
              <div key={p.title} className="principle-card card reveal-item">
                <h3>{p.title}</h3>
                <p>{p.detail}</p>
              </div>
            ))}
          </Reveal>
        </div>
      </section>

      <section className="section">
        <div className="container">
          <Reveal className="leadership-block card">
            <div className="leadership-photo-frame">
              <img src={directorPhoto} alt="Amartya Modi" className="leadership-photo" />
            </div>
            <div className="leadership-copy">
              <span className="eyebrow">Leadership</span>
              <h2>Backed by 20+ years in business automation.</h2>
              <p className="leadership-name">Amartya Modi — Director, Tecnvirons</p>
              <p>
                Tecnvirons is led by a director with over 20 years of experience in business
                building through marketing, sales, and factory and process automation — including
                hazardous-area application consulting across project, procurement, and installation
                teams. That operational background, working directly with field and maintenance
                teams, shapes how this platform is designed: for the realities of dispatch, on-site
                work, and service billing, not just office workflows.
              </p>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
